import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cloud, Download, ExternalLink, File as FileIcon, FileText, FolderOpen, HardDriveDownload, Image, Mail, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { readScopedLocalJson, writeScopedLocalJson } from '@/lib/offline-core';
import { useApp } from '@/contexts/AppContext';
import { useToast } from '@/hooks/use-toast';
import SectionShortcutsLauncher from '@/components/SectionShortcutsLauncher';
import { isTransientConnectionError } from '../../ui-foundation/core';
import {
  type DriveFile,
  deleteGoogleDriveFile,
  downloadDriveFile,
  ensureAdultingDriveStructure,
  fetchGoogleDriveFileViewLink,
  isGoogleDriveConfigured,
  listGoogleDriveImportableFiles,
  uploadFileToGoogleDrive,
} from '@/lib/google-drive';
import {
  DEFAULT_INTEGRATION_SETTINGS,
  loadIntegrationSettings,
  saveIntegrationSettings,
  type IntegrationSettingsRecord,
} from '@/lib/integration-settings';
import {
  EMAIL_INGESTION_FORWARD_ADDRESS,
  buildEmailIngestionSummary,
  loadEmailIngestionHistory,
  type EmailIngestionRecord,
} from '../../ui-foundation/content';
import { hasTrackedPets } from '../../ui-foundation/features/pets';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppEmptyState,
  AppIconButton,
  AppInput,
  AppPage,
  AppPanelButton,
  AppPageIntro,
  AppSectionHeader,
  AppText,
} from '../../ui-foundation';
import {
  AppDialog as Dialog,
  AppDialogContent as DialogContent,
  AppDialogDescription as DialogDescription,
  AppDialogHeader as DialogHeader,
  AppDialogTitle as DialogTitle,
} from '../../ui-foundation/adapters/web';

interface DocRecord {
  id: string;
  file_name: string;
  file_size: number;
  file_type: string;
  folder: string;
  storage_path: string;
  uploaded_at: string;
  notes: string | null;
  storage_provider?: string | null;
  external_id?: string | null;
  external_url?: string | null;
  external_parent_id?: string | null;
}

const DOCUMENT_CACHE_SCOPE = 'documents';

const FOLDERS = [
  { key: 'money', icon: '💰', label: 'Money', desc: 'Payslips, bank statements, tax documents', bg: 'bg-[#FFF8F0]', border: 'border-[#F0E6D6]' },
  { key: 'home', icon: '🏠', label: 'Home', desc: 'Tenancy agreement, insurance, utility bills', bg: 'bg-[#F0F7F9]', border: 'border-[#D6E8ED]' },
  { key: 'health', icon: '🧠', label: 'Health', desc: 'NHS letters, prescriptions, test results', bg: 'bg-[#F5EEF4]', border: 'border-[#E4D7E2]' },
  { key: 'pets', icon: '🐾', label: 'Pets', desc: 'Insurance, microchip, adoption, and vet paperwork', bg: 'bg-[#F9F6EF]', border: 'border-[#E9DFC7]' },
  { key: 'family', icon: '👨‍👩‍👧', label: 'People', desc: 'School letters, childcare documents', bg: 'bg-[#FEF9F0]', border: 'border-[#F0E8D0]' },
  { key: 'work', icon: '💼', label: 'Work', desc: 'Contracts, P60, references', bg: 'bg-[#F0F4F6]', border: 'border-[#D8E2E8]' },
] as const;

type FolderKey = typeof FOLDERS[number]['key'];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(type: string) {
  if (type.startsWith('image/')) return <Image size={18} className="text-teal" />;
  if (type === 'application/pdf') return <FileText size={18} className="text-destructive" />;
  return <FileIcon size={18} className="text-muted-foreground" />;
}

function providerLabel(doc: DocRecord) {
  return doc.storage_provider === 'google_drive' ? 'Google Drive' : 'Adulting';
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { user, data } = useApp();
  const { toast } = useToast();
  const [docs, setDocs] = useState<DocRecord[]>([]);
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pickFolder, setPickFolder] = useState<{ file: globalThis.File } | null>(null);
  const [driveImportOpen, setDriveImportOpen] = useState(false);
  const [driveImportTargetFolder, setDriveImportTargetFolder] = useState<FolderKey | null>(null);
  const [driveImportFiles, setDriveImportFiles] = useState<DriveFile[]>([]);
  const [driveImportLoading, setDriveImportLoading] = useState(false);
  const [driveImportError, setDriveImportError] = useState<string | null>(null);
  const [driveImportQuery, setDriveImportQuery] = useState('');
  const [driveImportingId, setDriveImportingId] = useState<string | null>(null);
  const [integrationSettings, setIntegrationSettings] = useState<IntegrationSettingsRecord | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [emailIngestionRows, setEmailIngestionRows] = useState<EmailIngestionRecord[]>([]);
  const [emailIngestionLoading, setEmailIngestionLoading] = useState(true);
  const [emailIngestionError, setEmailIngestionError] = useState<string | null>(null);
  const [hasPetProfiles, setHasPetProfiles] = useState(false);

  const usingGoogleDrive = integrationSettings?.documents_mode === 'google_drive';
  const usingAppleFiles = integrationSettings?.documents_mode === 'icloud';
  const emailIngestionSummary = useMemo(
    () => buildEmailIngestionSummary(emailIngestionRows, user?.email || null),
    [emailIngestionRows, user?.email],
  );

  const fetchDocs = useCallback(async () => {
    if (!user) {
      setDocs([]);
      return;
    }
    const { data: rows, error } = await supabase
      .from('documents')
      .select('*')
      .eq('user_id', user.id)
      .order('uploaded_at', { ascending: false });

    if (error) {
      const cached = readScopedLocalJson<DocRecord[]>(DOCUMENT_CACHE_SCOPE, user.id);
      if (isTransientConnectionError(error) && cached && cached.length) {
        setDocs(cached);
        toast({
          title: 'Showing saved documents',
          description: 'Adulting could not refresh your document list just now, so it kept the last loaded copy on screen.',
        });
        return;
      }

      toast({
        title: 'Could not load documents',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }

    const nextDocs = (rows || []) as unknown as DocRecord[];
    setDocs(nextDocs);
    writeScopedLocalJson(DOCUMENT_CACHE_SCOPE, user.id, nextDocs);
  }, [toast, user]);

  const fetchIntegrationSettings = useCallback(async () => {
    if (!user) {
      setIntegrationSettings(null);
      setSettingsLoading(false);
      return;
    }

    setSettingsLoading(true);
    try {
      const settings = await loadIntegrationSettings(user.id);
      setIntegrationSettings(settings);
    } catch (error) {
      console.error(error);
      setIntegrationSettings({
        user_id: user.id,
        ...DEFAULT_INTEGRATION_SETTINGS,
      });
    } finally {
      setSettingsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchDocs();
  }, [fetchDocs]);

  useEffect(() => {
    void fetchIntegrationSettings();
  }, [fetchIntegrationSettings]);

  useEffect(() => {
    let active = true;

    const loadEmailIngestion = async () => {
      if (!user) {
        if (active) {
          setEmailIngestionRows([]);
          setEmailIngestionError(null);
          setEmailIngestionLoading(false);
        }
        return;
      }

      if (active) {
        setEmailIngestionLoading(true);
      }

      const result = await loadEmailIngestionHistory(supabase, user.id);
      if (!active) return;
      setEmailIngestionRows(result.rows);
      setEmailIngestionError(result.errorMessage);
      setEmailIngestionLoading(false);
    };

    void loadEmailIngestion();

    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    let active = true;

    const loadPetVisibility = async () => {
      if (!user) {
        if (active) {
          setHasPetProfiles(false);
        }
        return;
      }

      const { data: petRows, error } = await supabase
        .from('pets')
        .select('name')
        .order('name');

      if (!active) return;
      if (error) {
        setHasPetProfiles(false);
        return;
      }

      setHasPetProfiles(hasTrackedPets((petRows || []) as Array<{ name?: string | null }>));
    };

    void loadPetVisibility();

    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    const refresh = () => {
      void fetchIntegrationSettings();
    };

    window.addEventListener('adulting:integration-settings-changed', refresh);
    return () => window.removeEventListener('adulting:integration-settings-changed', refresh);
  }, [fetchIntegrationSettings]);

  const ensureGoogleDriveReady = useCallback(async (folder: FolderKey) => {
    if (!user) {
      throw new Error('You need to be signed in to store documents.');
    }

    if (!isGoogleDriveConfigured()) {
      throw new Error('Google Drive is not configured in this build yet.');
    }

    const structure = await ensureAdultingDriveStructure({
      childNames: data.profile.children.map((child) => child.name),
    });

    const nextSettings = await saveIntegrationSettings(user.id, {
      documents_mode: 'google_drive',
      documents_root_folder_id: structure.rootFolder.id,
      documents_root_folder_name: structure.rootFolder.name,
      documents_root_folder_url: structure.rootFolder.webViewLink || null,
      documents_last_synced_at: new Date().toISOString(),
      documents_sync_status: 'ready',
      google_account_email: structure.userEmail,
    });

    setIntegrationSettings(nextSettings);

    if (folder === 'family') {
      return structure.familyGeneralFolder.id;
    }

    const parentFolder = structure.foldersByKey[folder];
    if (!parentFolder?.id) {
      throw new Error('Could not resolve the Google Drive folder for that document.');
    }

    return parentFolder.id;
  }, [data.profile.children, user]);

  const uploadFile = useCallback(async (file: globalThis.File, folder: FolderKey) => {
    if (!user) return;

    if (file.size > 50 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 50MB.', variant: 'destructive' });
      return;
    }

    setUploading(true);

    try {
      if (usingGoogleDrive) {
        const parentId = await ensureGoogleDriveReady(folder);
        const uploaded = await uploadFileToGoogleDrive({
          file,
          parentId,
        });

        const { error: dbError } = await supabase.from('documents').insert({
          user_id: user.id,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type || 'application/octet-stream',
          folder,
          storage_path: uploaded.id,
          storage_provider: 'google_drive',
          external_id: uploaded.id,
          external_url: uploaded.webViewLink || null,
          external_parent_id: parentId,
        } as any);

        if (dbError) {
          await deleteGoogleDriveFile(uploaded.id).catch(() => undefined);
          throw dbError;
        }

        toast({
          title: 'Uploaded to Google Drive',
          description: file.name,
        });
      } else {
        const storagePath = `${user.id}/${folder}/${Date.now()}_${file.name}`;
        const { error: uploadError } = await supabase.storage.from('user-documents').upload(storagePath, file);

        if (uploadError) {
          throw uploadError;
        }

        const { error: dbError } = await supabase.from('documents').insert({
          user_id: user.id,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type || 'application/octet-stream',
          folder,
          storage_path: storagePath,
          storage_provider: 'adulting',
          external_id: null,
          external_url: null,
          external_parent_id: null,
        } as any);

        if (dbError) {
          throw dbError;
        }

        toast({ title: 'Uploaded', description: file.name });
      }

      await fetchDocs();
      return true;
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Could not upload that file.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setUploading(false);
    }
  }, [ensureGoogleDriveReady, fetchDocs, toast, user, usingGoogleDrive]);

  const handleUpload = (folder: FolderKey) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        void uploadFile(file, folder);
      }
    };
    input.click();
  };

  const handleMainUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) setPickFolder({ file });
    };
    input.click();
  };

  const loadDriveImportFiles = useCallback(async (query = '') => {
    setDriveImportLoading(true);
    setDriveImportError(null);

    try {
      const files = await listGoogleDriveImportableFiles(query);
      setDriveImportFiles(files);
    } catch (error) {
      setDriveImportError(error instanceof Error ? error.message : 'Could not load your Google Drive files.');
      setDriveImportFiles([]);
    } finally {
      setDriveImportLoading(false);
    }
  }, []);

  const handleGoogleDriveImport = async (targetFolder?: FolderKey) => {
    if (!isGoogleDriveConfigured()) {
      toast({
        title: 'Google Drive not connected',
        description: 'This build still needs the Google web client ID before Drive import can run.',
        variant: 'destructive',
      });
      return;
    }

    const folder = targetFolder || 'work';
    setDriveImportTargetFolder(folder);
    setDriveImportQuery('');
    setDriveImportFiles([]);
    setDriveImportError(null);
    setDriveImportOpen(true);
    await loadDriveImportFiles('');
  };

  const handleImportDriveFile = useCallback(async (driveFile: DriveFile) => {
    const folder = driveImportTargetFolder || 'work';
    setDriveImportingId(driveFile.id);

    try {
      const blob = await downloadDriveFile(driveFile);
      const file = new globalThis.File([blob], driveFile.name, { type: driveFile.mimeType });
      const imported = await uploadFile(file, folder);

      if (!imported) {
        return;
      }

      toast({
        title: 'Imported from Google Drive',
        description: usingGoogleDrive
          ? `${driveFile.name} was added to ${FOLDERS.find((item) => item.key === folder)?.label || 'Documents'} in your organised Google Drive folders.`
          : `${driveFile.name} was added to your Adulting documents.`,
      });
      setDriveImportOpen(false);
    } catch (error) {
      toast({
        title: 'Import failed',
        description: error instanceof Error ? error.message : 'Could not import from Google Drive.',
        variant: 'destructive',
      });
    } finally {
      setDriveImportingId(null);
    }
  }, [driveImportTargetFolder, toast, uploadFile, usingGoogleDrive]);

  const deleteDoc = async (doc: DocRecord) => {
    if (!user) return;

    try {
      if (doc.storage_provider === 'google_drive') {
        const fileId = doc.external_id || doc.storage_path;
        if (fileId) {
          await deleteGoogleDriveFile(fileId);
        }
      } else {
        await supabase.storage.from('user-documents').remove([doc.storage_path]);
      }

      const { error } = await supabase.from('documents').delete().eq('id', doc.id);
      if (error) {
        throw error;
      }

      toast({ title: 'Deleted', description: doc.file_name });
      await fetchDocs();
    } catch (error) {
      toast({
        title: 'Could not delete document',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const downloadDoc = async (doc: DocRecord) => {
    try {
      if (doc.storage_provider === 'google_drive') {
        const link = doc.external_url || (doc.external_id ? await fetchGoogleDriveFileViewLink(doc.external_id) : null);
        if (link) {
          window.open(link, '_blank', 'noopener,noreferrer');
          return;
        }

        throw new Error('Could not open that Google Drive file.');
      }

      const { data: signedUrlData, error } = await supabase.storage.from('user-documents').createSignedUrl(doc.storage_path, 60);
      if (error) {
        throw error;
      }

      if (signedUrlData?.signedUrl) {
        window.open(signedUrlData.signedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      toast({
        title: 'Could not open document',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const visibleFolders = useMemo(
    () => FOLDERS.filter((folder) => folder.key !== 'pets' || hasPetProfiles),
    [hasPetProfiles],
  );

  useEffect(() => {
    if (!hasPetProfiles && activeFolder === 'pets') {
      setActiveFolder(null);
    }
  }, [activeFolder, hasPetProfiles]);

  const folderCounts = visibleFolders.reduce((acc, folder) => {
    acc[folder.key] = docs.filter((doc) => doc.folder === folder.key).length;
    return acc;
  }, {} as Record<string, number>);

  const folderDocs = activeFolder ? docs.filter((doc) => doc.folder === activeFolder) : [];
  const activeFolderMeta = FOLDERS.find((folder) => folder.key === activeFolder);
  const driveImportFolderMeta = FOLDERS.find((folder) => folder.key === (driveImportTargetFolder || 'work'));
  const driveStructureLabel = useMemo(() => {
    const childNames = data.profile.children.map((child) => child.name.trim()).filter(Boolean);
    const parts = ['Money', 'Home', 'Health'];
    if (hasPetProfiles) {
      parts.push('Pets');
    }
    parts.push('Family', 'Work', 'Education', 'Self employment & business');
    if (childNames.length > 0) {
      parts.push(...childNames);
    }
    return parts.join(', ');
  }, [data.profile.children, hasPetProfiles]);

  return (
    <AppPage className="animate-fade-in">
      {pickFolder && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPickFolder(null)} />
          <div className="relative bg-card rounded-2xl shadow-2xl p-5 w-[90%] max-w-sm animate-fade-in">
            <h3 className="text-base font-bold text-foreground mb-1">Choose a folder</h3>
            <p className="text-xs text-muted-foreground mb-4">Where should &quot;{pickFolder.file.name}&quot; go?</p>
            <div className="space-y-2">
              {visibleFolders.map((folder) => (
                <AppPanelButton
                  key={folder.key}
                  onClick={() => {
                    const file = pickFolder.file;
                    setPickFolder(null);
                    void uploadFile(file, folder.key);
                  }}
                  className="gap-3 border-card-border bg-card hover:bg-muted"
                >
                  <span className="text-lg">{folder.icon}</span>
                  <span className="text-sm font-medium text-foreground">{folder.label}</span>
                </AppPanelButton>
              ))}
            </div>
            <AppButton tone="outline" className="mt-4 w-full" onPress={() => setPickFolder(null)}>
              Cancel
            </AppButton>
          </div>
        </div>
      )}

      <Dialog
        open={driveImportOpen}
        onOpenChange={(open) => {
          setDriveImportOpen(open);
          if (!open) {
            setDriveImportTargetFolder(null);
            setDriveImportQuery('');
            setDriveImportError(null);
            setDriveImportFiles([]);
            setDriveImportingId(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import from Google Drive</DialogTitle>
            <DialogDescription>
              Bring an existing Drive file into {driveImportFolderMeta?.label || 'Work'} so Adulting keeps it in your organised document catalogue.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex gap-2">
              <AppInput
                value={driveImportQuery}
                onChange={(event) => setDriveImportQuery(event.target.value)}
                placeholder="Search your Google Drive"
              />
              <AppButton tone="outline" onPress={() => void loadDriveImportFiles(driveImportQuery)} disabled={driveImportLoading}>
                {driveImportLoading ? 'Searching…' : 'Search'}
              </AppButton>
            </div>

            {driveImportError ? (
              <AppCard backgroundColor="$background">
                <AppText fontSize="$sm" fontWeight="600">Google Drive import is blocked</AppText>
                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">{driveImportError}</AppText>
              </AppCard>
            ) : driveImportLoading ? (
              <AppCard backgroundColor="$background">
                <AppText fontSize="$sm">Loading recent Google Drive files…</AppText>
              </AppCard>
            ) : driveImportFiles.length === 0 ? (
              <AppEmptyState
                title="No importable Google Drive files found"
                body={driveImportQuery.trim() ? 'Try a different search term.' : 'Recent non-folder files from your Drive will appear here.'}
                action={(
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto">
                    <HardDriveDownload size={22} className="text-muted-foreground" />
                  </div>
                )}
              />
            ) : (
              <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
                {driveImportFiles.map((driveFile) => (
                  <AppCard key={driveFile.id} flexDirection="row" alignItems="center" gap="$sm">
                    {fileIcon(driveFile.mimeType)}
                    <div className="flex-1 min-w-0">
                      <AppText className="truncate" fontSize="$sm" fontWeight="600">{driveFile.name}</AppText>
                      <AppText fontSize="$xs" color="$colorMuted">
                        {driveFile.sizeBytes ? formatSize(driveFile.sizeBytes) : 'Google Drive file'}
                      </AppText>
                    </div>
                    <AppButton
                      size="sm"
                      tone="outline"
                      onPress={() => void handleImportDriveFile(driveFile)}
                      disabled={driveImportingId === driveFile.id || uploading}
                    >
                      {driveImportingId === driveFile.id ? 'Importing…' : 'Import'}
                    </AppButton>
                  </AppCard>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AppCard>
        <div className="flex items-start gap-3">
          <Cloud size={18} className="mt-0.5 shrink-0 text-primary" />
          <div className="flex-1 min-w-0">
            <AppText fontSize="$md" fontWeight="700">
              {settingsLoading
                ? 'Loading document storage…'
                : usingGoogleDrive
                  ? 'New documents go to your Google Drive'
                  : usingAppleFiles
                    ? 'Apple Files sync is enabled'
                  : 'New documents stay in Adulting storage'}
            </AppText>
            <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
              {usingGoogleDrive
                ? 'Adulting keeps the secure document catalogue here while new files can also be mirrored into your own Google Drive folder tree. Existing Adulting-hosted files still stay available here, so a mixed setup is fine.'
                : usingAppleFiles
                  ? 'Apple Files sync is handled in the Adulting iPhone app. Adulting still keeps the secure catalogue here, and the iPhone app can copy those same documents into your Apple Files folder when you want that extra path.'
                  : 'Files uploaded here stay in Adulting’s own secure cloud storage and sync across your Adulting devices. If you want, you can also connect Google Drive or use Apple Files sync from the iPhone app later.'}
            </AppText>
            {usingGoogleDrive ? (
              <AppCard backgroundColor="$background" className="mt-3">
                <AppText fontSize="$xs" fontWeight="700" color="$colorMuted" textTransform="uppercase" letterSpacing={1}>
                  Drive structure
                </AppText>
                <AppText fontSize="$sm">{driveStructureLabel}</AppText>
                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                  {integrationSettings?.google_account_email ? `Connected as ${integrationSettings.google_account_email}. ` : ''}
                  Root folder: {integrationSettings?.documents_root_folder_name || 'Adulting'}
                </AppText>
                <AppButtonRow className="mt-2">
                  {integrationSettings?.documents_root_folder_url ? (
                    <AppButton
                      tone="outline"
                      size="sm"
                      onPress={() => window.open(integrationSettings.documents_root_folder_url || '', '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink size={12} />
                      Open Drive folder
                    </AppButton>
                  ) : null}
                  <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
                    Manage in My Settings
                  </AppButton>
                </AppButtonRow>
              </AppCard>
            ) : usingAppleFiles ? (
              <AppCard backgroundColor="$background" className="mt-3">
                <AppText fontSize="$xs" fontWeight="700" color="$colorMuted" textTransform="uppercase" letterSpacing={1}>
                  Apple Files status
                </AppText>
                <AppText fontSize="$sm">
                  {integrationSettings?.documents_root_folder_name
                    ? `Selected in iPhone app · ${integrationSettings.documents_root_folder_name}`
                    : 'Synced from the Adulting iPhone app'}
                </AppText>
                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                  Adulting creates the organised Apple Files folder structure in the iPhone app. Web uploads here still stay in Adulting storage, so using both together is normal until a direct Apple storage path exists.
                </AppText>
                <AppButtonRow className="mt-2">
                  <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
                    Manage in My Settings
                  </AppButton>
                </AppButtonRow>
              </AppCard>
            ) : (
              <AppButton className="mt-3" tone="outline" size="sm" onPress={() => navigate('/profile')}>
                Manage in My Settings
              </AppButton>
            )}
          </div>
        </div>
      </AppCard>

      <AppCard>
        <div className="flex items-start gap-3">
          <Mail size={18} className="mt-0.5 shrink-0 text-primary" />
          <div className="flex-1 min-w-0">
            <AppText fontSize="$md" fontWeight="700">
              {emailIngestionSummary.headline}
            </AppText>
            <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
              Forward mail to {EMAIL_INGESTION_FORWARD_ADDRESS}. Adulting matches forwarded mail to {emailIngestionSummary.accountEmailLabel} before filing attachments and applying supported updates.
            </AppText>
            <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
              {emailIngestionLoading
                ? 'Checking recent forwarding activity…'
                : emailIngestionError
                  ? emailIngestionError
                  : emailIngestionRows.length
                    ? `Latest status: ${emailIngestionSummary.latestStatusLabel}`
                    : `Waiting for the first forwarded email to ${EMAIL_INGESTION_FORWARD_ADDRESS}.`}
            </AppText>

            <AppCard backgroundColor="$background" className="mt-3">
              <AppText fontSize="$xs" fontWeight="700" color="$colorMuted" textTransform="uppercase" letterSpacing={1}>
                Recent ingestion history
              </AppText>
              {emailIngestionLoading ? (
                <AppText className="mt-1" fontSize="$sm">
                  Loading recent forwarded mail…
                </AppText>
              ) : emailIngestionError ? (
                <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
                  History is not readable from this build yet. The forwarding instructions are still ready above.
                </AppText>
              ) : emailIngestionSummary.history.length ? (
                <div className="mt-2 space-y-2">
                  {emailIngestionSummary.history.map((item) => (
                    <AppCard key={item.id} backgroundColor="$background">
                      <AppText fontSize="$sm" fontWeight="600">
                        {item.title}
                      </AppText>
                      <AppText className="mt-0.5" fontSize="$xs" color="$colorMuted">
                        {item.meta}
                      </AppText>
                      <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                        {item.body}
                      </AppText>
                    </AppCard>
                  ))}
                </div>
              ) : (
                <AppText className="mt-1" fontSize="$sm" color="$colorMuted">
                  No forwarded emails yet. Once a message arrives, Adulting will show the most recent ingestion here.
                </AppText>
              )}
            </AppCard>
          </div>
        </div>
      </AppCard>

      {activeFolder && activeFolderMeta ? (
        <>
          <AppButton alignSelf="flex-start" tone="ghost" size="sm" onPress={() => setActiveFolder(null)}>
            <ArrowLeft size={16} /> All folders
          </AppButton>
          <AppSectionHeader
            title={`${activeFolderMeta.icon} ${activeFolderMeta.label}`}
            subtitle={activeFolderMeta.desc}
            action={(
              <AppButtonRow>
                <AppButton
                  tone="outline"
                  size="sm"
                  onPress={() => void handleGoogleDriveImport(activeFolder as FolderKey)}
                  disabled={uploading}
                >
                <HardDriveDownload size={12} />
                Drive
                </AppButton>
                <AppButton
                  size="sm"
                  onPress={() => handleUpload(activeFolder as FolderKey)}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading…' : usingGoogleDrive ? '+ Upload to Drive' : '+ Upload'}
                </AppButton>
              </AppButtonRow>
            )}
          />

          {folderDocs.length === 0 ? (
            <AppEmptyState
              title="No documents here yet"
              body="Upload the first one into this folder so the shared document shell stays complete."
              action={(
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto">
                <FolderOpen size={22} className="text-muted-foreground" />
                </div>
              )}
            />
          ) : (
            <div className="space-y-2">
              {folderDocs.map((doc) => (
                <AppCard key={doc.id} flexDirection="row" alignItems="center" gap="$sm">
                  {fileIcon(doc.file_type)}
                  <div className="flex-1 min-w-0">
                    <AppText className="truncate" fontSize="$sm" fontWeight="600">{doc.file_name}</AppText>
                    <AppText fontSize="$xs" color="$colorMuted">
                      {formatSize(doc.file_size)} · {providerLabel(doc)} · {new Date(doc.uploaded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </AppText>
                  </div>
                  <AppIconButton onClick={() => void downloadDoc(doc)} aria-label="Open">
                    <Download size={16} className="text-teal" />
                  </AppIconButton>
                  <AppIconButton onClick={() => void deleteDoc(doc)} aria-label="Delete">
                    <Trash2 size={16} className="text-destructive" />
                  </AppIconButton>
                </AppCard>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <AppPageIntro
            subtitle="My important files, all in one place."
            action={(
              <SectionShortcutsLauncher
                title="Document shortcuts"
                subtitle="Open the next document action without leaving a permanent toolbar on screen."
                items={[
                  {
                    id: 'upload',
                    label: usingGoogleDrive ? 'Upload to Google Drive' : 'Upload document',
                    description: usingGoogleDrive
                      ? 'Add a new file into the organised Adulting Drive folders.'
                      : 'Upload a file into Adulting-managed cloud storage.',
                    onPress: handleMainUpload,
                  },
                  {
                    id: 'drive-import',
                    label: 'Import from Google Drive',
                    description: 'Pull an existing Drive file into the right Adulting folder.',
                    onPress: () => void handleGoogleDriveImport(),
                  },
                ]}
              />
            )}
          />

          <div className="grid grid-cols-2 gap-3 mb-6 lg:grid-cols-3 xl:grid-cols-5">
            {visibleFolders.map((folder) => (
              <AppPanelButton
                key={folder.key}
                onClick={() => setActiveFolder(folder.key)}
                className={`${folder.bg} ${folder.border} rounded-[12px] border p-4`}
              >
                <span className="text-2xl block mb-2">{folder.icon}</span>
                <p className="text-sm font-semibold text-teal">{folder.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{folder.desc}</p>
                <p className="text-[10px] font-medium text-secondary mt-2">
                  {folderCounts[folder.key]} {folderCounts[folder.key] === 1 ? 'file' : 'files'}
                </p>
              </AppPanelButton>
            ))}
          </div>
        </>
      )}
    </AppPage>
  );
}
